import React, { useState, useEffect } from "react";
import { Form, Select, Button, Spin, InputNumber, Checkbox, Row, Col } from "antd";
import axios from "axios";
import { GoogleMap } from "../Common";

const fetchOptions = async url => {
	try {
		const response = await axios.get(url);
		return response.data.data.map(item => ({
			label: item.name,
			value: item.id
		}));
	} catch (error) {
		console.error("Error fetching options: ", error);
		return [];
	}
};

const PopulationGridContent = ({ polygon, isVisible, viewGoogleMap = false }) => {
	const [form] = Form.useForm();
	const [dataSources, setDataSources] = useState({
		behaviors: [],
		life_events: [],
		family_statuses: [],
		industries: [],
		interests: []
	});
	const [loading, setLoading] = useState(false);
	const [buttonLoading, setButtonLoading] = useState(false);
	const [estimatedReach, setEstimatedReach] = useState("N/A");
	const [circle, setCircle] = useState(null);

	const accessToken =
		"EAAGcSHpp6FMBO5RxQTgPoZBbtscMONX8MpYFklAMtK0lmOBGabbqyLi3g7uK8ieiIQ4KE5I6pqtJaPClPBfOfR6Q7ZAP85pMxcQDWa0wtenyCNhPP4j31SgxpTMF6gbpLa41tEuZCbimhZAOogY6DN5BlSo7IfMwvZCUGwkJB9NOHFGVAbQo12as39orWkZAX8"; // Replace with your token
	const apiVersion = "v13.0";
	const adAccountId = "act_770642423396404";
	const baseUrl = `https://graph.facebook.com/${apiVersion}`;

	useEffect(() => {
		if (polygon && polygon.coordinates.length > 0) {
			const coordinates = polygon.coordinates;
			let signedArea = 0;
			let centroidX = 0;
			let centroidY = 0;

			for (let i = 0; i < coordinates.length; i++) {
				const x0 = coordinates[i].lng;
				const y0 = coordinates[i].lat;
				const x1 = coordinates[(i + 1) % coordinates.length].lng;
				const y1 = coordinates[(i + 1) % coordinates.length].lat;

				const a = x0 * y1 - x1 * y0;
				signedArea += a;
				centroidX += (x0 + x1) * a;
				centroidY += (y0 + y1) * a;
			}

			signedArea *= 0.5;
			centroidX = centroidX / (6 * signedArea);
			centroidY = centroidY / (6 * signedArea);

			const distances = coordinates.map(coord => {
				const dx = coord.lat - centroidY;
				const dy = coord.lng - centroidX;
				return Math.sqrt(dx * dx + dy * dy);
			});

			let maxDistance = Math.max(...distances);
			if (maxDistance * 111.139 < 1) {
				maxDistance = 1 / 111.139;
			}

			const adjustedCircle = {
				center: { lat: centroidY, lng: centroidX },
				radius: maxDistance * 111139
			};

			setCircle(adjustedCircle);
		} else {
			setCircle(null);
		}
	}, [polygon]);

	const fetchDataForCategory = async category => {
		const data = await fetchOptions(
			`${baseUrl}/search?type=adTargetingCategory&class=${category}&access_token=${accessToken}`
		);

		if (category === "interests") {
			const dataToOmmit = [
				"Cosmetics (personal care)",
				"Science (science)",
				"Beauty (social concept)",
				"Adventure travel (travel & tourism)",
				"Small business (business & finance)",
				"Interior design (design)",
				"Tablet computers (computers & electronics)",
				"Aviation (air travel)",
				"Mexican cuisine (food & drink)",
				"Word games (games)",
				"Concerts (music event)",
				"Mobile phones (smart phone)",
				"Action games (video games)",
				"Camcorders (consumer electronics)",
				"Gambling (gambling)",
				"Tattoos (body art)",
				"Arts and music (art)",
				"Online poker (gambling)",
				"Games (leisure)",
				"Beauty salons (cosmetics)",
				"Animated movies (movies)",
				"Auto racing (motor sports)",
				"Online games (video games)",
				"Smartphones (consumer electronics)"
			];
			const filteredData = data.filter(({ label }) => {
				return dataToOmmit.includes(label);
			});
			setDataSources(prev => ({ ...prev, [category]: filteredData }));
			return;
		}

		if (category === "behaviors") {
			const dataToOmmit = [
				"Frequent",
				"Small",
				"Early",
				"Console",
				"People",
				"Recently",
				"Recently",
				"Internet"
			].map(item => item.toLowerCase());
			const filteredData = data.filter(({ label }) => {
				return dataToOmmit.some(omitWord => label.toLowerCase().includes(omitWord));
			});
			setDataSources(prev => ({ ...prev, [category]: filteredData }));
			return;
		}

		setDataSources(prev => ({ ...prev, [category]: data }));
	};

	useEffect(() => {
		if (isVisible) {
			setLoading(true);
			Promise.all(
				["behaviors", "life_events", "family_statuses", "industries", "interests"].map(async category => {
					if (!localStorage.getItem(category)) {
						await fetchDataForCategory(category);
					} else {
						setDataSources(prev => ({
							...prev,
							[category]: JSON.parse(localStorage.getItem(category))
						}));
					}
				})
			).finally(() => setLoading(false));
		}
	}, [isVisible]);

	useEffect(() => {
		if (!isVisible) {
			["behaviors", "life_events", "family_statuses", "industries", "interests"].forEach(category => {
				localStorage.removeItem(category);
			});
			setDataSources({
				behaviors: [],
				life_events: [],
				family_statuses: [],
				industries: [],
				interests: []
			});
			setEstimatedReach("N/A");
		}
	}, [isVisible]);

	const handleSearch = async () => {
		setButtonLoading(true);
		const formValues = form.getFieldsValue();
		try {
			const targetingSpec = Object.entries(formValues).reduce((acc, [key, value]) => {
				if (value) {
					if (key === "min_age") {
						acc["age_min"] = value;
					} else if (key === "max_age") {
						acc["age_max"] = value;
					} else if (key === "gender") {
						acc["genders"] = value; // value is already an array for Checkbox.Group
					} else if (key === "relationship_status") {
						acc["relationship_statuses"] = value;
					} else {
						acc[key] = value.map(item => {
							const sourceList = dataSources[key] || [];
							return {
								id: item,
								name: sourceList.find(source => source.value === item)?.label || item
							};
						});
					}
				}
				return acc;
			}, {});

			if (circle) {
				targetingSpec.geo_locations = {
					custom_locations: [
						{
							latitude: circle.center.lat,
							longitude: circle.center.lng,
							radius: Math.max(circle.radius / 1000, 1),
							distance_unit: "kilometer"
						}
					]
				};
			}

			const response = await axios.get(
				`${baseUrl}/${adAccountId}/reachestimate?access_token=${accessToken}&targeting_spec=${encodeURIComponent(
					JSON.stringify(targetingSpec)
				)}`
			);
			const { users_upper_bound } = response.data.data;
			// const populationOfCircle = polygon.filterData.radius_population;
			const populationOfCircle = polygon.filterData.population;

			const populationOfGrid = polygon.filterData.population;

			const ratio = users_upper_bound / populationOfCircle;
			const upper_bound_for_populationOfGrid = populationOfGrid * ratio;
			setEstimatedReach(Number(upper_bound_for_populationOfGrid).toFixed(0));
		} catch (error) {
			console.error("Error fetching reach estimate: ", error);
		} finally {
			setButtonLoading(false);
		}
	};

	const handleReset = () => {
		form.resetFields();
		setEstimatedReach("N/A");
		setCircle(null);
	};

	const dropdownRender = (menu, key) => (
		<div>
			{menu}
			<div style={{ display: "flex", justifyContent: "space-between", padding: "8px" }}>
				<span style={{ cursor: "pointer", color: "#1890ff" }} onClick={() => handleSelectAll(key)}>
					Select All
				</span>
				<span style={{ cursor: "pointer", color: "#1890ff" }} onClick={() => handleUnselectAll(key)}>
					Unselect All
				</span>
			</div>
		</div>
	);

	const handleSelectAll = key => {
		const allValues = dataSources[key].map(option => option.value);
		form.setFieldsValue({ [key]: allValues });
	};

	const handleUnselectAll = key => {
		form.setFieldsValue({ [key]: [] });
	};

	return (
		<Spin spinning={loading} tip="Loading...">
			{viewGoogleMap && (
				<GoogleMap
					zoomToPolygon
					center={circle?.center}
					polygons={[
						{
							...polygon,
							polygonClick: false,
							data: undefined
						}
					]}
					circles={[circle]}
				/>
			)}

			<Form form={form} layout="vertical">
				{Object.keys(dataSources).map(key => (
					<Form.Item
						name={key}
						label={key.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
						key={key}
					>
						<Select
							showSearch
							filterOption={(input, option) => option?.label?.toLowerCase().includes(input.toLowerCase())}
							placeholder={`Select ${key}`}
							options={dataSources[key]}
							mode="multiple"
							allowClear
							maxTagCount={3}
							dropdownRender={menu => dropdownRender(menu, key)}
						/>
					</Form.Item>
				))}

				{/* Gender Form Item */}
				<Form.Item name="gender" label="Gender">
					<Checkbox.Group
						options={[
							{ label: "Male", value: 1 },
							{ label: "Female", value: 2 }
						]}
					/>
				</Form.Item>

				{/* Relationship Status Form Item */}
				<Form.Item name="relationship_status" label="Relationship Status">
					<Select
						options={[
							{ label: "Single", value: 1 },
							{ label: "In a Relationship", value: 2 },
							{ label: "Married", value: 3 },
							{ label: "Engaged", value: 4 },
							{ label: "Not Specified", value: 6 },
							{ label: "In a Civil Union", value: 7 },
							{ label: "In a Domestic Partnership", value: 8 },
							{ label: "In an Open Relationship", value: 9 },
							{ label: "It's Complicated", value: 10 },
							{ label: "Separated", value: 11 },
							{ label: "Divorced", value: 12 },
							{ label: "Widowed", value: 13 }
						]}
						placeholder="Select Relationship Status"
						mode="multiple"
						allowClear
					/>
				</Form.Item>

				{/* Age Form Items */}
				<Row gutter={16}>
					<Col span={12}>
						<Form.Item name="min_age" label="Minimum Age">
							<InputNumber min={13} max={65} placeholder="Min Age" />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item name="max_age" label="Maximum Age">
							<InputNumber min={13} max={65} placeholder="Max Age" />
						</Form.Item>
					</Col>
				</Row>
			</Form>
			<div style={{ marginTop: 20, textAlign: "center" }}>
				<Button type="primary" onClick={handleSearch} loading={buttonLoading} style={{ marginRight: 10 }}>
					View Reach
				</Button>
				<Button onClick={handleReset}>Reset</Button>
			</div>
			<div style={{ marginTop: 20, textAlign: "center" }}>
				<p style={{ color: estimatedReach === "Too Low" ? "red" : "inherit" }}>
					Estimated Audience: {estimatedReach}
				</p>
			</div>
		</Spin>
	);
};

export default PopulationGridContent;
